import React from "react"
import styled from "@emotion/styled"
import { css } from "@emotion/react"
import Section from "src/components/Section"
import Grid from "src/components/Grid"
import TextLockup from "src/components/TextLockup"
import Logo from "src/components/Logo"
import { mq, typography, globals } from "src/styles"

const WrapperSection = styled(Section)`
  padding-left: 20px;
  padding-right: 20px;
  ${({ isFirstSection }) => (isFirstSection ? "" : "")}
  ${globals.verticalSpacing("padding-top")}
  ${globals.verticalSpacing("padding-bottom")}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => (hideMobile && "display: none")}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => (hideDesktop && "display: none")}
  }
`

const Inner = styled.div`
  max-width: 1536px;
  margin: 0 auto;
`

const Headline = styled.div`
  margin-bottom: 20px;
  ${mq.largeAndUp} {
    .text-new {
      max-width: 37.5rem;
      margin: 0 auto;
    }
  }
`

const ColumnWrapper = styled.div``
const CardWrapper = styled.div`
  display: flex;
  grid-template-columns: 1fr;
  width: 100%;

  margin: 0 auto;

  ${globals.verticalSpacing("padding-top", 0.75)}

  ${mq.largeAndUp} {
    padding-top: 0;
  }

  ${mq.extraLargeAndUp} {
    max-width: 475px;
  }
`

const Table = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  p {
    margin: 0 !important;
  }
`

const THead = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;
`

const Th = styled.div`
  display: flex;
  flex-direction: column;
  &:nth-child(1) {
    min-width: 140px;
  }
`

const THRow = styled.div`
  display: flex;
  border: none;
  box-shadow: none;
  padding: 0 !important;
  margin-bottom: 8px;
  text-align: left;
  gap: 8px;
  padding: 0px 16px !important;

  > * {
    width: calc(100% / 3);
  }
`

const Tr = styled.div`
  display: flex;
  gap: 8px;

  &:not(:last-child) {
    padding-bottom: 8px;
    border-bottom: 1px solid #8d8d8d;
  }
`

const Td = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% / 2);
`

const TBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;
`

const Card = styled.div`
  display: flex;
  box-shadow: ${globals.shadow()};
  padding: 20px 16px !important;
  border-radius: 4px;
  overflow: hidden;

  gap: 8px;
  ${({ border }) =>
    border ? `border: 1px solid #3D6EDF;` : `border: 1px solid #d1d1d6;`}

  &:not(:last-child) {
    margin-bottom: 14px;
  }

  > div:nth-child(1) {
    padding-right: 8px;
    min-width: 140px;
  }

  > div:nth-child(2) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
`

const TextWrapper = styled.div`
  ${mq.largeAndUp} {
    .text-new {
      max-width: 37.5rem;
      margin: 0 auto;
    }
  }
`
const LogoWrapper = styled.div`
  width: 110px;
  height: auto;
  margin-bottom: 5px;
`

const Financing = ({
  className,
  theme,
  hideMobile,
  hideDesktop,
  prevTheme,
  nextTheme,
  eyebrow,
  newStyle,
  _rawHeadline,
  _rawText,
  actions,
  columnTitles,
  cards,
  isFirstSection,
  id,
}) => {
  return (
    <WrapperSection
      className={className}
      setTheme={theme}
      prevTheme={prevTheme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      fullWidth
      id={id}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      <Inner>
        <Grid small="[1]" large="[6] [6]" columnGap={true} vAlign="top">
          <ColumnWrapper newStyle={newStyle}>
            <Headline>
              <TextLockup
                newStyle={newStyle}
                entranceDelay={1}
                text={_rawHeadline}
                theme={theme}
              />
            </Headline>
            <TextWrapper>
              <TextLockup
                entranceDelay={1.25}
                text={_rawText}
                actions={actions}
                theme={theme}
                eyebrow={eyebrow}
                newStyle={newStyle}
              />
            </TextWrapper>
          </ColumnWrapper>

          <CardWrapper>
            <Table>
              <THead>
                <THRow>
                  <Th>{""}</Th>
                  {columnTitles.map(c => (
                    <Th
                      css={css`
                        font-weight: 600;
                        color: var(--light-text-color);
                      `}
                      key={c}
                    >
                      {c}
                    </Th>
                  ))}
                </THRow>
              </THead>
              <TBody>
                {cards.map((c, idx) => (
                  <Card border={idx == 0} key={c.title + idx}>
                    <div>
                      {c.showLogo && (
                        <LogoWrapper>
                          <Logo />
                        </LogoWrapper>
                      )}

                      <p
                        css={css`
                          font-weight: 600;
                          color: var(--main-color);
                        `}
                      >
                        {c.title}
                      </p>
                    </div>
                    <div>
                      {c.rows.map((r, idx) => {
                        return (
                          <Tr key={r.title + idx}>
                            <Td>
                              <p
                                css={css`
                                  font-weight: 600;
                                  color: var(--text-color);
                                `}
                              >
                                {r.title}
                              </p>
                            </Td>
                            <Td>
                              <p
                                css={css`
                                  color: var(--text-color);
                                `}
                              >
                                {r.value}
                              </p>
                            </Td>
                          </Tr>
                        )
                      })}
                    </div>
                  </Card>
                ))}
              </TBody>
            </Table>
          </CardWrapper>
        </Grid>
      </Inner>
    </WrapperSection>
  )
}

export default Financing
